
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { updatePropertyStatus, updateProperty } from "@/services/propertyService";
import { geocodeAddress } from "@/services/geocodingService";

// Parse a YYYY-MM-DD string as a local Date (avoids UTC-midnight timezone drift)
const parseLocalDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return new Date(value);
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
};

// Format a Date as a local YYYY-MM-DD string (no UTC conversion)
const formatLocalDate = (date: Date): string => format(date, "yyyy-MM-dd");

interface PropertyState {
  id: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  price: string;
  arv: string;
  bedrooms: string;
  bathrooms: string;
  squareFeet: string;
  lotSize: string;
  yearBuilt: string;
  foundationType: string;
  roofAge: string;
  windowType: string;
  furnaceAge: string;
  waterHeaterAge: string;
  architectureStyle: string;
  propertyType: string;
  units: string;

  parkingType: string;
  description: string;
  zillowLink: string;
  status: string;
  contractExpirationDate: Date;
  video_url: string | null;
  video_thumbnail_url: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface ClosedData {
  closedDate: Date;
  salePrice: string;
  financingType: string;
}

export const useEditProperty = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [wasSoldOnLoad, setWasSoldOnLoad] = useState(false);
  const [wasPendingOnLoad, setWasPendingOnLoad] = useState(false);
  const [wasDraftOnLoad, setWasDraftOnLoad] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);
  
  // Track original address for detecting changes
  const [originalAddress, setOriginalAddress] = useState({
    address: "",
    city: "",
    state: "",
    zipCode: ""
  });
  
  const [property, setProperty] = useState<PropertyState>({
    id: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    price: "",
    arv: "",
    bedrooms: "",
    bathrooms: "",
    squareFeet: "",
    lotSize: "",
    yearBuilt: "",
    foundationType: "",
    roofAge: "",
    windowType: "",
    furnaceAge: "",
    waterHeaterAge: "",
    architectureStyle: "",
    propertyType: "",
    units: "",

    parkingType: "",
    description: "",
    zillowLink: "",
    status: "active",
    contractExpirationDate: new Date(),
    video_url: null,
    video_thumbnail_url: null,
    latitude: null,
    longitude: null
  });
  
  const [closedData, setClosedData] = useState<ClosedData>({
    closedDate: new Date(),
    salePrice: "",
    financingType: ""
  });

  const [propertyImages, setPropertyImages] = useState<Array<{
    id: string;
    image_url: string;
    is_primary: boolean;
    display_order: number;
  }>>([]);
  
  // Handle coordinates change from GoogleMapPreview
  const handleCoordinatesChange = useCallback((lat: number, lng: number) => {
    setProperty(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  }, []);
  
  const fetchPropertyData = async () => {
    if (!id) return;
    
    try {
      setIsFetching(true);
      
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) {
        toast({
          title: "Error",
          description: "Failed to load property data.",
          variant: "destructive"
        });
        console.error("Error fetching property:", error);
        return;
      }
      
      if (data) {
        // Track if property was sold when loaded (for protection logic)
        const isSoldOnLoad = !!(data.closed_date && data.status === "sold");
        setWasSoldOnLoad(isSoldOnLoad);
        
        // Track if property was pending when loaded (for reactivation logic)
        const isPendingOnLoad = data.status === "pending";
        setWasPendingOnLoad(isPendingOnLoad);

        // Track if property was draft when loaded (drives Status tab activation UI)
        setWasDraftOnLoad(data.status === "draft");
        
        // Determine effective status based on contract expiration and current status
        let effectiveStatus = data.status || "active";
        const contractExpirationDate = parseLocalDate(data.contract_expiration_date) ?? new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        contractExpirationDate.setHours(0, 0, 0, 0);
        
        // If contract has expired, property is not sold, and current status is active or pending, set to expired
        if (contractExpirationDate <= today && !data.closed_date && 
            (data.status === "active" || data.status === "pending")) {
          effectiveStatus = "expired";
        }

        // Store original address for change detection
        setOriginalAddress({
          address: data.address || "",
          city: data.city || "",
          state: data.state || "",
          zipCode: data.zip_code || ""
        });

        setProperty({
          id: data.id,
          address: data.address || "",
          city: data.city || "",
          state: data.state || "",
          zipCode: data.zip_code || "",
          price: data.price ? data.price.toString() : "",
          arv: data.arv_value ? data.arv_value.toString() : "",
          bedrooms: data.bedrooms ? data.bedrooms.toString() : "",
          bathrooms: data.bathrooms ? data.bathrooms.toString() : "",
          squareFeet: data.square_feet ? data.square_feet.toString() : "",
          lotSize: data.lot_size ? parseInt(data.lot_size.replace(/[^0-9]/g, '') || '0').toLocaleString() : "",
          yearBuilt: data.year_built ? data.year_built.toString() : "",
          foundationType: data.foundation_type || "",
          roofAge: data.roof_age || "",
          windowType: (data as any).window_type || "",
          furnaceAge: data.furnace_age || "",
          waterHeaterAge: data.water_heater_age || "",
          architectureStyle: data.architecture_style || "",
          propertyType: data.property_type || "",
          units: (data as any).units != null ? String((data as any).units) : "",

          parkingType: data.parking_type || "",
          description: data.description || "",
          zillowLink: data.zillow_link || "",
          status: effectiveStatus,
          contractExpirationDate: contractExpirationDate,
          video_url: (data as any).video_url || null,
          video_thumbnail_url: (data as any).video_thumbnail_url || null,
          latitude: (data as any).latitude || null,
          longitude: (data as any).longitude || null
        });
        
        if (data.closed_date) {
          setClosedData({
            closedDate: parseLocalDate(data.closed_date) ?? new Date(),
            salePrice: data.sale_price ? data.sale_price.toString() : "",
            financingType: (data as any).financing_type || ""
          });
        }
      }

      const { data: imagesData, error: imagesError } = await supabase
        .from('property_images')
        .select('*')
        .eq('property_id', id)
        .order('display_order');
      
      if (imagesError) {
        console.error("Error fetching property images:", imagesError);
      } else {
        setPropertyImages(imagesData || []);
      }
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading property data.",
        variant: "destructive"
      });
    } finally {
      setIsFetching(false);
    }
  };
  
  useEffect(() => {
    fetchPropertyData();
  }, [id, toast]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProperty(prev => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setProperty(prev => ({ ...prev, [name]: value }));
    clearFieldError(name);
    if (name === 'propertyType' && value !== 'multi-unit') clearFieldError('units');
  };
  
  const handleStatusChange = (status: string) => {
    setProperty(prev => ({ ...prev, status }));
  };
  
  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      setProperty(prev => ({ ...prev, contractExpirationDate: date }));
      clearFieldError('contractDate');
    }
  };
  
  const handleClosedDateChange = (date: Date | undefined) => {
    if (date) {
      setClosedData(prev => ({ ...prev, closedDate: date }));
    }
  };
  
  const handleClosedDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setClosedData(prev => ({ ...prev, [name]: value }));
  };
  
  // Check if address has changed
  const hasAddressChanged = () => {
    return (
      property.address !== originalAddress.address ||
      property.city !== originalAddress.city ||
      property.state !== originalAddress.state ||
      property.zipCode !== originalAddress.zipCode
    );
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guard: block submit if still a draft — seller must change status first
    if (property.status === 'draft') {
      toast({
        title: "Draft not activated",
        description: "Complete all required fields, set the status to Active, then click Activate Listing.",
        variant: "destructive"
      });
      return;
    }
    
    // Validation: active/pending status requires future contract expiration date
    if (property.status === "active" || property.status === "pending") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expDate = new Date(property.contractExpirationDate);
      expDate.setHours(0, 0, 0, 0);
      
      if (expDate <= today) {
        setErrors((prev) => ({ ...prev, contractDate: "Contract expiration date must be in the future for active and pending properties." }));
        toast({
          title: "Validation Error",
          description: "Contract expiration date must be in the future for active and pending properties.",
          variant: "destructive"
        });
        return;
      }
    }

    // Validation: Multi Unit requires a units count > 1
    if (property.propertyType === 'multi-unit') {
      const unitsNum = parseInt(property.units, 10);
      if (!property.units || isNaN(unitsNum) || unitsNum <= 1) {
        setErrors((prev) => ({ ...prev, units: "Number of units is required for Multi Unit properties and must be greater than 1." }));
        toast({
          title: "Validation Error",
          description: "Number of units is required for Multi Unit properties and must be greater than 1.",
          variant: "destructive"
        });
        return;
      }
    }

    setErrors({});

    
    setIsLoading(true);
    
    try {
      if (!id) throw new Error("Property ID is missing");
      
      // Re-geocode if address changed and we don't have coordinates
      let finalLatitude = property.latitude;
      let finalLongitude = property.longitude;
      
      if (hasAddressChanged()) {
        const geocodeResult = await geocodeAddress(
          property.address,
          property.city,
          property.state,
          property.zipCode
        );
        
        if (geocodeResult) {
          finalLatitude = geocodeResult.lat;
          finalLongitude = geocodeResult.lng;
        }
      }
      
      const propertyData = {
        address: property.address,
        city: property.city,
        state: property.state,
        zip_code: property.zipCode,
        price: parseFloat(property.price.replace(/[$,]/g, '')),
        arv_value: property.arv ? parseFloat(property.arv.replace(/[$,]/g, '')) : null,
        bedrooms: parseInt(property.bedrooms),
        bathrooms: parseFloat(property.bathrooms),
        square_feet: parseInt(property.squareFeet),
        lot_size: property.lotSize.replace(/,/g, ''),
        year_built: property.yearBuilt ? parseInt(property.yearBuilt) : null,
        foundation_type: property.foundationType,
        roof_age: property.roofAge,
        window_type: property.windowType || null, // Fix: NULL instead of empty string
        furnace_age: property.furnaceAge,
        water_heater_age: property.waterHeaterAge,
        architecture_style: property.architectureStyle,
        property_type: property.propertyType,
        units: property.propertyType === 'multi-unit' && property.units ? parseInt(property.units, 10) : null,

        parking_type: property.parkingType,
        description: property.description,
        zillow_link: property.zillowLink,
        status: property.status,
        contract_expiration_date: formatLocalDate(property.contractExpirationDate),
        latitude: finalLatitude,
        longitude: finalLongitude
      };
      
      const { success, error } = await updateProperty(id, propertyData);
      
      if (!success) {
        throw new Error(error || "Failed to update property");
      }
      
      if (property.status === "sold") {
        const closedUpdateData = {
          closedDate: formatLocalDate(closedData.closedDate),
          salePrice: closedData.salePrice ? parseFloat(closedData.salePrice) : undefined,
          financingType: closedData.financingType || undefined
        };
        
        const { success: statusSuccess, error: statusError } = await updatePropertyStatus(
          id, 
          "sold", 
          closedUpdateData
        );
        
        if (!statusSuccess) {
          toast({
            title: "Warning",
            description: "Property updated but there was an issue updating the closed data.",
            variant: "destructive"
          });
        }
      }
      
      toast({
        title: "Success",
        description: "Property updated successfully."
      });
      
      navigate(`/seller/properties/${id}`, { replace: true });
    } catch (error) {
      console.error("Error updating property:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!id) return;
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);
        
      if (error) {
        throw new Error(error.message);
      }
      
      toast({
        title: "Success",
        description: "Property deleted successfully."
      });
      
      navigate("/seller/properties/active");
    } catch (error) {
      console.error("Error deleting property:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete property",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isDraft = property.status === 'draft';

  const handleSaveDraft = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const safeInt = (val: string) => { const n = parseInt(val); return isNaN(n) ? null : n; };
      const safeFloat = (val: string) => { const n = parseFloat(val.replace(/[$,]/g, '')); return isNaN(n) ? null : n; };

      const draftData: Record<string, unknown> = {
        address: property.address || null,
        city: property.city || null,
        state: property.state || null,
        zip_code: property.zipCode || null,
        price: safeFloat(property.price) ?? 0,
        arv_value: safeFloat(property.arv),
        bedrooms: safeInt(property.bedrooms) ?? 0,
        bathrooms: safeFloat(property.bathrooms) ?? 0,
        square_feet: safeInt(property.squareFeet) ?? 0,
        lot_size: property.lotSize.replace(/,/g, '') || null,
        year_built: safeInt(property.yearBuilt),
        foundation_type: property.foundationType || null,
        roof_age: property.roofAge || null,
        window_type: property.windowType || null,
        furnace_age: property.furnaceAge || null,
        water_heater_age: property.waterHeaterAge || null,
        architecture_style: property.architectureStyle || null,
        property_type: property.propertyType || null,
        units: property.propertyType === 'multi-unit' && property.units ? parseInt(property.units, 10) : null,

        parking_type: property.parkingType || null,
        description: property.description || null,
        zillow_link: property.zillowLink || null,
        status: 'draft',
        contract_expiration_date: property.contractExpirationDate
          ? formatLocalDate(property.contractExpirationDate)
          : null,
        latitude: property.latitude,
        longitude: property.longitude,
      };

      const { success, error } = await updateProperty(id, draftData as Parameters<typeof updateProperty>[1]);
      if (!success) throw new Error(error || "Failed to save draft");

      toast({ title: "Draft saved", description: "Your draft has been saved." });
      navigate(`/seller/properties/active`, { replace: true });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save draft",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    id,
    property,
    closedData,
    propertyImages,
    isLoading,
    isFetching,
    wasSoldOnLoad,
    wasPendingOnLoad,
    wasDraftOnLoad,
    isDraft,
    fetchPropertyData,
    handleChange,
    handleSelectChange,
    handleStatusChange,
    handleDateChange,
    handleClosedDateChange,
    handleClosedDataChange,
    handleCoordinatesChange,
    handleSaveDraft,
    handleSubmit,
    handleDelete,
    errors,
    clearFieldError,
  };
};
